import React, { useState, useEffect, useCallback } from 'react';

const ConcessionaireView = ({ apiUrl, token, activeTab }) => {
    const [stock, setStock] = useState([]);
    const [movements, setMovements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [adjustForm, setAdjustForm] = useState({ itemId: '', qty: '' });
    const [addItemForm, setAddItemForm] = useState({ id: '', desc: '', qty: '', unit: 'Unidades' });
    const [isAiLoading, setIsAiLoading] = useState(false);
    
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const headers = { 'Authorization': `Bearer ${token}` };
            const [stockRes, movRes] = await Promise.all([
                fetch(`${apiUrl}/api/getSheetData?sheetName=Stock`, { headers }),
                fetch(`${apiUrl}/api/getSheetData?sheetName=Movimientos`, { headers })
            ]);
            const stockData = await stockRes.json();
            const movData = await movRes.json();
            setStock(stockData.slice(1).map((row, index) => ({ data: row, index: index + 2 })));
            setMovements(movData.slice(1));
        } catch (err) {
            console.error("Error fetching data:", err);
            setError("No se pudieron cargar los datos. Revisa tu conexión o intenta recargar la página.");
        } finally {
            setLoading(false);
        }
    }, [apiUrl, token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleAddNewItem = async (e) => {
        e.preventDefault();
        const { id, desc, qty, unit } = addItemForm;
        if (!id || !desc || !qty || !unit) {
            alert("Por favor, complete todos los campos para agregar un nuevo ítem.");
            return;
        }
        try {
            const newRow = [id, desc, qty, unit];
            await fetch(`${apiUrl}/api/appendRow`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ sheetName: 'Stock', rowData: newRow }),
            });
            alert(`Ítem ${id} agregado correctamente.`);
            setAddItemForm({ id: '', desc: '', qty: '', unit: 'Unidades' });
            fetchData();
        } catch (err) {
            console.error("Error adding item:", err);
            alert("Error al agregar el ítem.");
        }
    };

    const handleAdjustStock = async (e) => {
        e.preventDefault();
        if (!adjustForm.itemId || !adjustForm.qty) {
            alert("Por favor, ingrese el ID del item y la cantidad a ajustar.");
            return;
        }
        try {
            const itemToAdjust = stock.find(item => item.data[0] === adjustForm.itemId);
            if (!itemToAdjust) {
                alert(`Item con ID '${adjustForm.itemId}' no encontrado.`);
                return;
            }
            const currentQty = parseInt(itemToAdjust.data[2]);
            const adjustment = parseInt(adjustForm.qty);
            const newQty = currentQty + adjustment;
            if (newQty < 0) {
                alert("El stock no puede ser negativo.");
                return;
            }
            const updatedStockRow = [...itemToAdjust.data];
            updatedStockRow[2] = newQty.toString();
            
            await fetch(`${apiUrl}/api/updateRow`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ sheetName: 'Stock', rowIndex: itemToAdjust.index, rowData: updatedStockRow }),
            });

            const movId = `MOV-AJUSTE-${new Date().toISOString().replace(/[-:T.Z]/g, '')}`;
            const fechaHora = new Date().toLocaleString('es-AR');
            const responsable = "Concesionario";
            
            const newMovementRow = [movId, fechaHora, "N/A", adjustForm.itemId, "Ajuste de Stock", adjustment, responsable];

            await fetch(`${apiUrl}/api/appendRow`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ sheetName: 'Movimientos', rowData: newMovementRow }),
            });

            alert(`Stock del item ${adjustForm.itemId} ajustado a ${newQty}.`);
            setAdjustForm({ itemId: '', qty: '' });
            fetchData();
        } catch (err) {
            console.error("Error adjusting stock:", err);
            alert("Error al ajustar el stock.");
        }
    };

    // --- NUEVA FUNCIÓN PARA EDITAR DESCRIPCIÓN ---
    const handleEditDescription = async (itemId, currentDescription) => {
        const newDescription = prompt(`Editar descripción para ${itemId}:`, currentDescription);
        if (!newDescription || newDescription === currentDescription) {
            return;
        }
        try {
            const response = await fetch(`${apiUrl}/api/update-stock-item`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ itemId, newDescription }),
            });
            const data = await response.json();
            if (response.ok) {
                alert(data.message);
                fetchData();
            } else {
                alert(`Error: ${data.error}`);
            }
        } catch (error) {
            console.error(error);
            alert("Error al actualizar la descripción.");
        }
    };

    if (loading) return <p>Cargando datos...</p>;
    if (error) return <div className="card"><h3>Error</h3><p>{error}</p></div>;

    if (activeTab === 'Gestión de Stock') {
        return (
            <>
                <div className="card">
                    <h3>Agregar Nuevo Ítem con Asistencia de IA</h3>
                    <form onSubmit={handleAddNewItem}>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <input type="text" placeholder="Descripción coloquial" value={addItemForm.desc} onChange={(e) => setAddItemForm({...addItemForm, desc: e.target.value})} style={{flex: 1}} required />
                            <button type="button" onClick={async () => {
                                if (!addItemForm.desc) { alert("Escribe una descripción primero."); return; }
                                setIsAiLoading(true);
                                try {
                                    const res = await fetch(`${apiUrl}/api/ai-suggest-item`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ description: addItemForm.desc }) });
                                    const suggestion = await res.json();
                                    if(res.ok) setAddItemForm({...addItemForm, desc: suggestion.formal_description, id: suggestion.suggested_id, unit: suggestion.suggested_unit});
                                    else alert(`Error de IA: ${suggestion.error}`);
                                } catch(err) { alert("No se pudo conectar con el servicio de IA."); }
                                finally { setIsAiLoading(false); }
                            }} disabled={isAiLoading} className="secondary">
                                {isAiLoading ? '🤖 Pensando...' : '🤖 IA'}
                            </button>
                        </div>
                        <input type="text" placeholder="ID del Ítem (se autogenera)" value={addItemForm.id} onChange={(e) => setAddItemForm({...addItemForm, id: e.target.value})} required />
                        <input type="number" placeholder="Cantidad Inicial" value={addItemForm.qty} onChange={(e) => setAddItemForm({...addItemForm, qty: e.target.value})} required />
                        <select value={addItemForm.unit} onChange={(e) => setAddItemForm({...addItemForm, unit: e.target.value})} required>
                            {['Unidades', 'Caja', 'Paquete', 'Metro', 'Kilogramo', 'Litro', 'Par', 'Rollo', 'Tubo', 'Otro'].map(unit => <option key={unit} value={unit}>{unit}</option>)}
                        </select>
                        <button type="submit">Agregar Ítem</button>
                    </form>
                </div>
                <div className="card">
                    <h3>Ajustar Stock</h3>
                    <form onSubmit={handleAdjustStock}>
                        <input type="text" placeholder="ID del Item a ajustar" value={adjustForm.itemId} onChange={(e) => setAdjustForm({...adjustForm, itemId: e.target.value})} />
                        <input type="number" placeholder="Cantidad (ej: +10 o -5)" value={adjustForm.qty} onChange={(e) => setAdjustForm({...adjustForm, qty: e.target.value})} />
                        <button type="submit">Ajustar Stock</button>
                    </form>
                </div>
                <div className="card">
                    <h3>Stock Actual</h3>
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Descripción</th>
                                    <th>Cantidad</th>
                                    <th>Unidad</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stock.map(item => (
                                    <tr key={item.index}>
                                        <td>{item.data[0]}</td>
                                        <td>{item.data[1]}</td>
                                        <td>{item.data[2]}</td>
                                        <td>{item.data[3]}</td>
                                        <td>
                                            <button onClick={() => handleEditDescription(item.data[0], item.data[1])}>
                                                Editar Descripción
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </>
        );
    }

    if (activeTab === 'Auditoría') {
        return (
            <div className="card">
                <h3>Auditoría de Movimientos</h3>
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>OT</th>
                                <th>Item</th>
                                <th>Tipo</th>
                                <th>Cantidad</th>
                                <th>Responsable</th>
                            </tr>
                        </thead>
                        <tbody>
                            {movements.map((mov, i) => <tr key={i}><td>{mov[1]}</td><td>{mov[2]}</td><td>{mov[3]}</td><td>{mov[4]}</td><td>{mov[5]}</td><td>{mov[6]}</td></tr>)}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    return null; // Para cualquier otra pestaña no manejada aquí
};

export default ConcessionaireView;